import type {
  ShipmentPropertyNames,
  ShipmentApi,
} from "@services/shipments/types";

// Date formatting utilities
const DatePropertyNames = new Set<ShipmentPropertyNames>([
  "eta",
  "lfd",
  "doc_cut_off_date",
  "port_cut_off_date",
  "empty_return_date",
  "delivery_appointment_date",
]);

export function isDateProperty(propertyName: ShipmentPropertyNames): boolean {
  return DatePropertyNames.has(propertyName);
}

export function formatDateValue(dateString: string): string {
  try {
    if (!dateString || dateString.trim() === "") {
      return "";
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch (error) {
    return dateString;
  }
}

// Property names in the preferred order for display
export const PROPERTY_NAMES: ShipmentPropertyNames[] = [
  "booking_number",
  "shipper",
  "consignee",
  "job_number",
  "house_bill_of_lading",
  "master_bill_of_lading",
  "origin",
  "destination",
  "shipping_line",
  "port_of_loading",
  "port_of_discharge",
  "carrier",
  "vessel_name",
  "voyage_number",
  "release_number",
  "container_number",
  "container_type",
  "seal_numbers",
  "number_of_containers",
  "invoice_number",
  "eta",
  "lfd",
  "doc_cut_off_date",
  "port_cut_off_date",
  "empty_return_date",
  "delivery_appointment_date",
  "unknown_id",
  "reference_number",
];

/**
 * Sort property names according to the preferred order defined in PROPERTY_NAMES
 */
export function sortPropertyNames(
  propertyNames: ShipmentPropertyNames[],
): ShipmentPropertyNames[] {
  return propertyNames.sort((a, b) => {
    const indexA = PROPERTY_NAMES.indexOf(a);
    const indexB = PROPERTY_NAMES.indexOf(b);

    // If both are in the list, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the list, prioritize the one in the list
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the list, sort alphabetically
    return a.localeCompare(b);
  });
}

/**
 * Convert property name to pretty display format
 */
export function labelFromPropertyName(
  propertyName: ShipmentPropertyNames,
): string {
  // Special labels for date fields (more user-friendly)
  const DATE_LABELS: Partial<Record<ShipmentPropertyNames, string>> = {
    eta: "ETA",
    lfd: "Last Free Day (LFD)",
    doc_cut_off_date: "Doc Cut Off Date",
    port_cut_off_date: "Port Cut Off Date",
    empty_return_date: "Empty Return Date",
    delivery_appointment_date: "Delivery Appointment",
  };

  if (DATE_LABELS[propertyName]) {
    return DATE_LABELS[propertyName]!;
  }

  if (
    propertyName === "house_bill_of_lading" ||
    propertyName === "master_bill_of_lading"
  ) {
    return "BL code(s)";
  }
  const PLURAL_NAMES = [
    "reference_number",
    "container_number",
    "container_type",
    "invoice_number",
  ] as ShipmentPropertyNames[];
  let name = propertyName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  if (PLURAL_NAMES.includes(propertyName)) {
    name += "(s)";
  }
  return name;
}

/**
 * Get unique property values for a given property name from shipment properties
 */
export function getPropertyValues(
  shipment: ShipmentApi,
  propertyName: ShipmentPropertyNames,
): string[] {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === propertyName,
  );

  if (matchingProperties.length === 0) return [];

  // Get unique values
  return Array.from(new Set(matchingProperties.map((prop) => prop.value)));
}

/**
 * Format property values as a comma-separated string
 * @param values Array of property values
 * @param propertyName Optional property name to enable special formatting (e.g., dates)
 */
export function formatPropertyValues(
  values: string[],
  propertyName?: ShipmentPropertyNames,
): string {
  const uniqueValues = Array.from(new Set(values));

  // Check if this is a date property and format accordingly
  if (propertyName && isDateProperty(propertyName)) {
    return uniqueValues.map(formatDateValue).join(", ");
  }

  return uniqueValues.join(", ");
}

export interface ShipmentDetail {
  label: string;
  propertyNames: ShipmentPropertyNames[]; // Original property names from the database
  values: string[];
  displayValue: string;
}

/**
 * Extract and format shipment details from properties
 */
export function getShipmentDetailsFromProperties(
  shipment: ShipmentApi,
): ShipmentDetail[] {
  // Get all unique property names and sort them
  const uniquePropertyNames = Array.from(
    new Set(shipment.properties.map((prop) => prop.name)),
  ) as ShipmentPropertyNames[];

  const sortedPropertyNames = sortPropertyNames(uniquePropertyNames).filter(
    (name) => name !== "unknown_id", // Exclude unknown_id from details
  );

  const labelNamesToPropertyNames: {
    [key: string]: ShipmentPropertyNames[];
  } = {};
  sortedPropertyNames.forEach((propertyName) => {
    const formattedName = labelFromPropertyName(propertyName);
    if (!labelNamesToPropertyNames[formattedName]) {
      labelNamesToPropertyNames[formattedName] = [];
    }
    labelNamesToPropertyNames[formattedName].push(propertyName);
  });

  return Object.keys(labelNamesToPropertyNames)
    .map((formattedName) => {
      const propertyNames = labelNamesToPropertyNames[formattedName];
      const values = propertyNames.flatMap((propertyName) =>
        getPropertyValues(shipment, propertyName),
      );
      // Pass the first property name to enable date formatting
      const displayValue = formatPropertyValues(values, propertyNames[0]);

      return {
        label: formattedName,
        propertyNames, // Include the original property names
        values,
        displayValue,
      };
    })
    .filter((detail) => detail.values.length > 0); // Only include properties that have values
}
